import React, { useState, useContext, useEffect, useMemo } from 'react';
import style from './NestedTable.scss';
import {
  isPrimitiveType,
  isEnumType,
  memberIsNotEmpty,
  getMemberFromType,
  generateEnumDoc,
  isParamInOneOfGroup,
  isMemberOfSomeOneOfGroup,
  getParamsForNextDepth,
  getParamAnnotationStrategy,
  removeNullTypeFromRequiredFields,
} from './utils';
import { FirstTableRow } from './FirstTableRow';
import { NestedTableNameDisplay } from './NestedTableNameDisplay';
import { TableColumn } from './TableColumn';
import { extractType } from '../utils';
import { ReactMarkdownDisplay } from '../../PageElements/ReactMarkdownDisplay';
import { shouldAbleExpand } from './NestedTableNameDisplay/utils';
import classnames from 'classnames';
import { MultipleTypesNestedTable } from './MultipleTypesNestedTable';
import { Caption } from '../../PageElements/Caption';
import { isLegacyType } from '../typeParser';
import { NestedTableTypeDisplay } from './NestedTableTypeDisplay';
import { useSelector } from 'react-redux';
import { API_TYPES } from '../../ApiDisplay';
import { StoreContext } from '../../../appContext/StoreProvider';
import { selectSiteActiveLayoutType } from '../../../../common/redux/features/siteConfig/selectors';
import { Parameter } from '../../../../types';
import { PARAM_ANNOTATION_STRATEGY } from './types';

interface NestedTableParam extends Parameter {
  insideOneOf: boolean;
  nativeType: string;
  readOnly: boolean;
  requiredFields: string[];
  fieldMask: 'REST' | 'RPC' | undefined;
  deprecated: boolean;
  metaData: any;
}
interface NestedTableProps {
  params: NestedTableParam[];
  title?: string;
  tableDepth?: number;
  dataHook?: string;
  isOneOf?: boolean;
  oneOfTableDepth?: number;
  isOneOfRequired?: boolean;
  isOneOfOptional?: boolean;
  isMainEntity?: boolean;
  isResponse?: boolean;
  isRequest?: boolean;
  path?: string;
  prefix?: string;
  oneOfGroups?: any[] | undefined;
  pageName?: string;
  hasRest?: boolean;
  forceAddFirstRow?: boolean;
  isAutoVeloDocs?: boolean;
  JSLayoutType?: boolean;
}

export const NestedTable = (props: NestedTableProps) => {
  const [openTableIndexes, setOpenTableIndexes] = useState({});
  const [enterAnimation, setEnterAnimation] = useState(false);
  const [selectedTypeIndex, setSelectedTypeIndex] = useState(0);

  const { store } = useContext(StoreContext);
  const paramAnnotationStrategy = useMemo(
    () => getParamAnnotationStrategy(store.viewer, props.JSLayoutType),
    [store.viewer],
  );

  const activeLayoutType = useSelector(selectSiteActiveLayoutType);
  const {
    title,
    tableDepth,
    dataHook,
    isOneOf,
    oneOfTableDepth,
    isOneOfRequired,
    isOneOfOptional,
    isRequest,
    isMainEntity,
    isAutoVeloDocs,
    JSLayoutType,
  } = props;

  const isFirstLevelOneOf = isOneOf && tableDepth === 0;
  const isOneOfSection = isOneOf && tableDepth === oneOfTableDepth;

  useEffect(() => {
    if (tableDepth > 0) {
      setTimeout(() => setEnterAnimation(true), 5);
    }
  }, []);

  return (
    <div
      data-hook={dataHook || 'nested-table'}
      role="table"
      aria-label="Object-attribute"
      className={classnames({
        [style.tableWrapper]: true,
        [style.oneOf]: isOneOf && tableDepth !== 0,
        [style.animate]: enterAnimation,
        [style[`depth-${tableDepth}`]]: shouldEnterDepth(),
        [style.oneOfSection]: isOneOfSection && tableDepth !== 0,
      })}
    >
      {shouldDisplayFirstRow() && !isFirstLevelOneOf && (
        <FirstTableRow
          title={title}
          columnTitles={['NAME', 'TYPE', 'DESCRIPTION']}
        />
      )}

      {oneOfCaption()}
      {generateRows()}
    </div>
  );

  function generateRows() {
    const { params = [], path, prefix, oneOfGroups, pageName } = props;

    const { beforeOneOf, afterOneOf } = getBeforeAndAfterOneOfIndex(params);

    const oneOfGroupRows =
      oneOfGroups?.map((oneOfGroup) => {
        const oneOfTableParams = params.filter(({ name: paramName }) =>
          isParamInOneOfGroup(paramName)(oneOfGroup),
        );
        oneOfTableParams.forEach((oneOfParam) => {
          oneOfParam.insideOneOf = true;
          // in oneOfGroup, the given annotations of "optional" and "required" are ignored.
          oneOfParam.optional = false;
          oneOfParam.required = false;
        });
        return (
          <NestedTable
            {...props}
            oneOfGroups={undefined}
            key={oneOfGroup.name}
            params={oneOfTableParams}
            oneOfTableDepth={tableDepth}
            isOneOf={true}
          />
        );
      }) || [];

    const relevantRows = getRelevantRows(params, oneOfGroups);
    const paramsRows =
      relevantRows?.map(
        (
          {
            name,
            type,
            nativeType,
            doc,
            deprecated,
            required,
            readOnly,
            optional,
            metaData,
            insideOneOf,
            requiredFields,
          },
          rowIndex,
        ) => {
          const relevantType =
            isRequest && required && type
              ? removeNullTypeFromRequiredFields(type)
              : type || nativeType;

          const member =
            getMemberFromType(relevantType, store) ||
            getMemberFromType(relevantType?.[0], store);

          const isUnionType =
            member?.members?.length === 1 &&
            !member.members[0].name &&
            Array.isArray(member.members[0].type);

          const isEnum = isEnumType(member);
          const classes = getRowClassesBasedOnOpenTables(rowIndex);
          const shouldBeAbleToExpand =
            shouldAbleExpand(store.docsTree, relevantType, metaData, store) &&
            !isUnionType;

          const insideOneOfDepthClasses = style[`inside-one-of-${tableDepth}`];

          return (
            <React.Fragment key={`${name}_param__nestedTable_${tableDepth}`}>
              {!isOneOfType(member) && (
                <div
                  role="row"
                  className={classnames({
                    [style.pointerCursor]: shouldBeAbleToExpand,
                    [style.row]: true,
                    [classes]: true,
                    [style.first]: rowIndex === 0,
                    [insideOneOfDepthClasses]: insideOneOf,
                    [style.insideOneOf]: insideOneOf,
                    [style.beforeOneOf]: rowIndex === beforeOneOf,
                    [style.afterOneOf]: rowIndex === afterOneOf,
                  })}
                  onClick={
                    shouldBeAbleToExpand
                      ? toggleTables(rowIndex, relevantType, metaData)
                      : undefined
                  }
                  data-hook={`row-${name}`}
                >
                  <TableColumn tableDepth={tableDepth} index={1}>
                    <span className="notranslate">
                      <NestedTableNameDisplay
                        name={name}
                        isRotated={isTableOpen(rowIndex)}
                        shouldBeAbleToExpand={shouldBeAbleToExpand}
                        {...getAnnotationProps(
                          member,
                          deprecated,
                          required,
                          optional,
                          readOnly,
                        )}
                      />
                    </span>
                  </TableColumn>
                  <TableColumn index={2}>
                    <div
                      className={`notranslate ${style.typeColumn}`}
                      data-hook="type-column-content"
                    >
                      {/* @ts-ignore */}
                      <NestedTableTypeDisplay
                        type={
                          isUnionType
                            ? member?.members?.[0]?.type
                            : relevantType
                        }
                        metaData={metaData}
                        path={path}
                        onTypeClicked={onTypeClicked(rowIndex)}
                        JSLayoutType={JSLayoutType}
                      />
                    </div>
                  </TableColumn>
                  <TableColumn index={3}>
                    <ReactMarkdownDisplay
                      markdown={`${
                        (isEnum && generateEnumDoc(member)) || ''
                      }\n\n${doc ? doc : ''}`}
                      textSize="small"
                      prefix={prefix}
                      mdName={pageName}
                      allowRerenders={true}
                      className={style.descriptionColumn}
                    />
                  </TableColumn>
                </div>
              )}
              {shouldRenderTable(
                relevantType,
                rowIndex,
                member,
                isOneOfType(member),
                requiredFields,
                required,
                optional,
                isUnionType,
              )}
            </React.Fragment>
          );
        },
      ) || [];

    return [...paramsRows, ...oneOfGroupRows];
  }

  function getAnnotationProps(
    member: any,
    deprecated: boolean,
    required: boolean,
    optional: boolean,
    readOnly: boolean,
  ): {
    deprecated: boolean;
    required: boolean;
    optional: boolean;
    readOnly: boolean;
  } {
    return {
      deprecated: deprecated || (deprecated !== false && member?.deprecated), // we will show deprecated annotetion only when the param or the member that the param is point on are deprecated.
      required:
        isRequest &&
        paramAnnotationStrategy === PARAM_ANNOTATION_STRATEGY.REQUIRED &&
        required, // we will show required annotation only when presenting request params and annotation strategy is "REQUIRED".
      optional:
        isRequest &&
        paramAnnotationStrategy === PARAM_ANNOTATION_STRATEGY.OPTIONAL &&
        optional, // we will show optional annotation only when presenting request params and annotation strategy is "OPTIONAL".
      readOnly: isMainEntity && readOnly, // we will show readOnly annotation only when presenting main entity params(object)
    };
  }

  function oneOfCaption() {
    return (
      isOneOfSection && (
        <div
          className={style.oneOfTitle}
          data-hook={`one-of-label-${tableDepth}`}
        >
          <Caption>ONE OF:</Caption>
          {isOneOfOptional && (
            <Caption dataHook="optional-label-oneof-group">Optional</Caption>
          )}
          {isOneOfRequired && (
            <Caption color="yellow" dataHook="required-label-oneof-group">
              Required
            </Caption>
          )}
        </div>
      )
    );
  }

  function isObjectMember(type, member) {
    return (
      (!Array.isArray(type) ||
        (Array.isArray(type) && type?.[0].referenceType)) &&
      !isPrimitiveType(type, store) &&
      memberIsNotEmpty(member)
    );
  }

  function isObjectWithReadOnlyParams(type, member, requiredFields) {
    if (isObjectMember(type, member)) {
      const paramsForNextDepth = getParamsForNextDepth({
        requiredFields,
        member,
        shouldAddOptionalAnnotation: isAutoVeloDocs,
      });
      const hasReadOnlyParams = paramsForNextDepth.every(
        (param) => param.readOnly,
      );

      return hasReadOnlyParams;
    }
  }

  function isFirstLevel() {
    return tableDepth === 0;
  }

  function hasIndex(index) {
    return openTableIndexes.hasOwnProperty(index);
  }

  function isTableOpen(index) {
    return openTableIndexes[index];
  }

  function addTableIndex(index, paramType, metaData) {
    if (Array.isArray(paramType)) {
      /*
      Sometimes, when the param type is an array (aka the type is a union type),
      there could be a case where the first item in the array is a primitive.
      Therefore, we make sure that the multiple types table will be opened on the first reference type
      in the array (since primitives don't have anything to present in the multiple types table).
      */
      const _selectedTypeIndex = paramType.findIndex((singleType) =>
        shouldAbleExpand(
          store.docsTree,
          isLegacyType(singleType) ? extractType(singleType) : singleType,
          metaData,
          store,
        ),
      );

      setSelectedTypeIndex(_selectedTypeIndex);
      setOpenTableIndexes((prevState) => ({ ...prevState, [index]: true }));
    } else {
      setOpenTableIndexes((prevState) => ({ ...prevState, [index]: true }));
    }
  }

  function toggleExistingTableIndex(index) {
    setOpenTableIndexes((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  }

  function toggleTables(index, paramType, metaData) {
    return () => {
      if (hasIndex(index)) {
        toggleExistingTableIndex(index);
      } else {
        addTableIndex(index, paramType, metaData);
      }
    };
  }

  function isRowSeparatedFromTable(rowIndex, predicate) {
    const openTables = (key) => openTableIndexes[key];
    return Object.keys(openTableIndexes)
      .filter(openTables)
      .map(Number)
      .some((openIndex) => predicate(openIndex, rowIndex));
  }

  function getRowClassesBasedOnOpenTables(rowIndex) {
    const fromBelow = (openIndex, _rowIndex) => openIndex === _rowIndex;
    const fromAbove = (openIndex, _rowIndex) => openIndex + 1 === _rowIndex;
    let classes = '';
    if (isRowSeparatedFromTable(rowIndex, fromBelow)) {
      classes += style.separatedFromBelow;
    }
    if (isRowSeparatedFromTable(rowIndex, fromAbove)) {
      classes += ` ${style.separatedFromAbove}`;
    }
    return classes;
  }

  function isOneOfType(member) {
    return member?.oneOf;
  }

  function getBeforeAndAfterOneOfIndex(
    params,
  ): {
    beforeOneOf?: number;
    afterOneOf?: number;
  } {
    let indexs = {};
    params.forEach(({ type, nativeType }, i) => {
      const relevantType = type || nativeType;
      const member = getMemberFromType(relevantType, store);
      if (isOneOfType(member)) {
        indexs = { beforeOneOf: i - 1, afterOneOf: i + 1 };
      }
    });
    return indexs;
  }

  function getRelevantRows(params: NestedTableParam[], oneOfGroups) {
    return params?.filter(
      ({
        name,
        type,
        nativeType,
        readOnly,
        required,
        requiredFields,
        fieldMask,
      }) => {
        const relevantType = type || nativeType;
        const member = getMemberFromType(relevantType, store);
        const isMemberOfOneOfGroup = isMemberOfSomeOneOfGroup(
          name,
          oneOfGroups,
        );
        const _isObjectWithReadOnlyParams =
          isRequest &&
          isObjectWithReadOnlyParams(relevantType, member, requiredFields);

        /*
          "fieldMask": "RPC" means the parameter should only be shown in RPC view.
          "fieldMask": "REST" means the parameter should only be shown in REST view.
          If it doesn't appear should be shown in both.
        */
        const shouldShowFieldMask = !fieldMask || fieldMask === getActiveType();

        /*
          We will always show params in table for the following:
          1. If the nested table is NOT for presenting Request, means it's for Main Entity or Response. For both we will show all fields.
          2. Required - if it's required field
          3. NOT readOnly - if it's not readOnly field
        */
        const shouldShowParam = !isRequest || required || !readOnly;

        return (
          shouldShowParam &&
          shouldShowFieldMask &&
          !isMemberOfOneOfGroup &&
          !_isObjectWithReadOnlyParams
        );
      },
    );
  }

  function getActiveType() {
    const { hasRest } = props;
    /*
      when activeLayoutType is RPC_REST we should know what is the actual activeType for the current operation.
    */

    if (activeLayoutType === API_TYPES.RPC_REST) {
      if (!hasRest) {
        return API_TYPES.RPC;
      }
      return API_TYPES.REST;
    }

    return activeLayoutType;
  }

  function onTypeClicked(rowIndex) {
    return (typeIndex) => () => {
      setSelectedTypeIndex(typeIndex);
      setOpenTableIndexes((prevState) => ({ ...prevState, [rowIndex]: true }));
    };
  }

  function onTypeDropdownItemSelect(typeIndex) {
    setSelectedTypeIndex(typeIndex);
  }

  function shouldRenderTable(
    type,
    index,
    member,
    isOneOfMember,
    requiredFields = [],
    required,
    optional,
    isUnionType,
  ) {
    if (Array.isArray(type) && isTableOpen(index)) {
      return (
        <MultipleTypesNestedTable
          {...props}
          tableDepth={tableDepth + 1}
          types={type}
          selectedTypeIndex={selectedTypeIndex}
          onTypeDropdownItemSelect={onTypeDropdownItemSelect}
          requiredFields={requiredFields}
          isAutoVeloDocs={isAutoVeloDocs}
          JSLayoutType={JSLayoutType}
        />
      );
    }

    if (isObjectMember(type, member) && isTableOpen(index)) {
      const params = getParamsForNextDepth({
        requiredFields,
        member,
        shouldAddOptionalAnnotation: isAutoVeloDocs,
      });
      return (
        <NestedTable
          {...props}
          oneOfGroups={member?.extra?.oneOfGroups}
          tableDepth={tableDepth + 1}
          params={params}
          isAutoVeloDocs={isAutoVeloDocs}
          JSLayoutType={JSLayoutType}
        />
      );
    }

    if (isOneOfMember) {
      const params = isUnionType
        ? member.members[0].type
        : getParamsForNextDepth({
            requiredFields,
            member,
            shouldAddOptionalAnnotation: isAutoVeloDocs,
          });

      if (params.length > 0) {
        params.forEach((param) => {
          param.insideOneOf = true;
        });
        return (
          <NestedTable
            {...props}
            oneOfGroups={member?.extra?.oneOfGroups}
            params={params}
            oneOfTableDepth={tableDepth}
            isOneOf={true}
            isOneOfRequired={required}
            isOneOfOptional={optional}
            isAutoVeloDocs={isAutoVeloDocs}
            JSLayoutType={JSLayoutType}
          />
        );
      }
    }

    return null;
  }

  function shouldDisplayFirstRow() {
    return isFirstLevel() || props.forceAddFirstRow;
  }

  function shouldEnterDepth() {
    return props.isOneOf ? tableDepth % 2 !== 0 : true;
  }
};
